import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { collection, query, where, getDocs } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import './CustomerDataReport.css';

function formatDate(ts) {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function CustomerDataReport() {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterMonth, setFilterMonth] = useState('');
    const [filterYear,  setFilterYear]  = useState('');

    useEffect(() => {
        fetchCustomers();
    }, []);

    async function fetchCustomers() {
        setLoading(true);
        try {
            const snap = await getDocs(
                query(collection(db, 'users'), where('role', '==', 'customer'))
            );
            const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
            setCustomers(data);
        } catch (err) {
            console.error('Error fetching customers:', err);
            setCustomers([]);
        }
        setLoading(false);
    }

    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);
    
    const filteredCustomers = customers.filter((c) => {
        if (!filterMonth && !filterYear) return true;
        if (!c.createdAt) return false;
        const d = c.createdAt.toDate ? c.createdAt.toDate() : new Date(c.createdAt);
        const monthMatch = filterMonth ? d.getMonth() + 1 === parseInt(filterMonth) : true;
        const yearMatch  = filterYear  ? d.getFullYear()  === parseInt(filterYear)  : true;
        return monthMatch && yearMatch;
    });

    const totalCustomers = customers.length;

    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const newThisMonth = customers.filter((c) => {
        if (!c.createdAt) return false;
        const d = c.createdAt.toDate ? c.createdAt.toDate() : new Date(c.createdAt);
        return d >= startOfMonth;
    }).length;

    function handleExport() {
        const rows = customers.map((c) => ({
            'Full Name': c.displayName || `${c.firstName || ''} ${c.lastName || ''}`.trim() || '—',
            'Email': c.email || '—',
            'Contact Number': c.contactNumber || c.phone || '—',
            'Date Registered': formatDate(c.createdAt),
        }));
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Stats');
        const today = new Date().toISOString().split('T')[0];
        XLSX.writeFile(wb, `JG_Stats_${today}.xlsx`);
    }

    return (
        <div className="report-section">
            {/* Header */}
            <div className="report-header">
                <h2 className="report-title">Customer Registry</h2>
                <button className="report-export-btn" onClick={handleExport} title="Export to Excel">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                </button>
            </div>

            {/* Stat Cards */}
            <div className="report-stat-cards report-stat-cards--2">
                <div className="stat-card stat-card--green">
                    <span className="stat-card__label">Total Customers</span>
                    <svg className="stat-card__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                    </svg>
                    <span className="stat-card__value">{totalCustomers}</span>
                </div>
                <div className="stat-card stat-card--slate">
                    <span className="stat-card__label">New this month</span>
                    <svg className="stat-card__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                    </svg>
                    <span className="stat-card__value">{newThisMonth}</span>
                </div>
            </div>

            {/* Filters */}
            <div className="report-filters">
                <select
                    className="report-filter-select"
                    value={filterMonth}
                    onChange={e => setFilterMonth(e.target.value)}
                >
                    <option value="">All Months</option>
                    {['January','February','March','April','May','June',
                        'July','August','September','October','November','December']
                        .map((m, i) => (
                        <option key={i+1} value={i+1}>{m}</option>
                    ))}
                </select>
                <select
                    className="report-filter-select"
                    value={filterYear}
                    onChange={e => setFilterYear(e.target.value)}
                >
                    <option value="">All Years</option>
                    {yearOptions.map(y => (
                        <option key={y} value={y}>{y}</option>
                    ))}
                </select>
                {(filterMonth || filterYear) && (
                    <button className="report-filter-clear" onClick={() => { setFilterMonth(''); setFilterYear(''); }}>
                        ✕ Clear
                    </button>
                )}
            </div>

{/* Table */}
<div className="report-table-wrapper">
                <table className="report-table">
                    <thead>
                        <tr>
                            <th>Full Name</th>
                            <th>Email</th>
                            <th>Contact Number</th>
                            <th>Date Registered</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={4} className="report-table__empty">Loading...</td></tr>
                        ) : filteredCustomers.length === 0 ? (
                            <tr><td colSpan={4} className="report-table__empty">No customers found.</td></tr>
                        ) : (
                            filteredCustomers.map((customer) => (
                                <tr key={customer.id}>
                                    <td className="customer-name">
                                        {customer.displayName ||
                                            `${customer.firstName || ''} ${customer.lastName || ''}`.trim() ||
                                            '—'}
                                    </td>
                                    <td>{customer.email || '—'}</td>
                                    <td>{customer.contactNumber || customer.phone || '—'}</td>
                                    <td>{formatDate(customer.createdAt)}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            {!loading && filteredCustomers.length > 0 && (
                <p className="report-result-count">
                    Showing {filteredCustomers.length} customer{filteredCustomers.length !== 1 ? 's' : ''}
                    {(filterMonth || filterYear) ? ' (filtered)' : ''}
                </p>
            )}
        </div>
    );
}